/**
 * This module contains functions for calculating metrics in reports.
 */

/**
 * Calculate net production (count).
 * @param {Number} grossProduction
 * @param {Number} rejectionCount
 * @return {Number}
 */
export function calcNetProductionCount(grossProduction, rejectionCount) {
    return grossProduction - rejectionCount;
}

/**
 * Calculate the planned production (in units).
 * @param {Number} plannedRate
 * @param {Number} operatingTime (running duration + performance downtime duration)
 * @return {Number}
 */
export function calcPlannedProduction(plannedRate, operatingTime) {
    return (plannedRate * operatingTime) / 60;
}

/**
 * Calculate production run rate (in minute).
 * @param {Number} nbProducts
 * @param {Number} operatingTime
 * @return {Number}
 */
export function calcProductionRate(nbProducts, operatingTime) {
    if (operatingTime === 0) {
        return 0;
    } else {
        return (nbProducts / operatingTime) * 60;
    }
}

/**
 * Calculate availability (percent).
 * @param {Number} availDowntimeDuration
 * @param {Number} operatingTime (running duration + performance downtime duration)
 * @return {Number}
 */
export function calcAvailability(availDowntimeDuration, operatingTime) {
    const availableTime = availDowntimeDuration + operatingTime;
    if (availableTime === 0) {
        // there was no downtime, so availability is 100%
        return 1;
    } else {
        return operatingTime / availableTime;
    }
}

/**
 * Calculate performance (percent).
 * @param {*} actualRunRate
 * @param {*} plannedRunRate
 */
export function calcPerformance(actualRunRate, plannedRunRate) {
    if (plannedRunRate === 0) {
        return 1;
    } else {
        return actualRunRate / plannedRunRate;
    }
}

/**
 * Calculate quality (percent).
 * @param {Number} grossProduction
 * @param {Number} rejectionCount
 * @return {Number}
 */
export function calcQuality(grossProduction, rejectionCount) {
    const netProduction = grossProduction - rejectionCount;
    if (grossProduction === 0) {
        if (rejectionCount === 0) {
            // nothing was produced, so quality is 100%
            return 1;
        } else {
            // could indicate issues related to production sensor, as there is no product to reject when the gross production is 0
            return 0;
        }
    } else {
        return netProduction / grossProduction;
    }
}

/**
 * Calculate OEE (percent).
 * @param {Number} availability
 * @param {Number} performance
 * @param {Number} quality
 * @return {Number}
 */
export function calcOEE(availability, performance, quality) {
    return availability * performance * quality;
}

/**
 * Calculate utilization (percent).`
 * @param {Number} runningDuration
 * @param {Number} totalDuration
 * @return {Number}
 */
export function calcUtilization(runningDuration, totalDuration) {
    if (totalDuration <= 0 || runningDuration < 0) {
        return 0; // Return 0 if inputs are invalid
    }
    return runningDuration / totalDuration;
}

/**
 * Calculate TEEP (percent).
 * @param {Number} utilization
 * @param {Number} performance
 * @param {Number} quality
 * @return {Number}
 */
export function calcTEEP(utilization, performance, quality) {
    // Ensure inputs are within valid range [0, 1]
    if (utilization === 0 || performance === 0 || quality === 0) {
        return 0; // Return 0 if any input is invalid
    }
    return utilization * performance * quality;
}
